import React, { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Alert, Button, Col, Form, Pagination, Row } from "react-bootstrap";
import { MdAdd, MdDelete, MdEdit } from "react-icons/md";
import { AuthContext } from "../../../contexts/AuthContext";
import { tempUrl } from "../../../contexts/ContextProvider";
import {
  ConfirmDeleteDialog,
  InfoDialog,
  Loader,
  ScrollToTop,
  SearchBar,
} from "../../../components";
import { ShowTableDaftarUser } from "../../../components/ShowTable";

const DaftarUser = () => {
  const { user, dispatch } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const selectedId = location.pathname.split("/")[2];

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [totalPage, setTotalPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const handleAuthError = useCallback((err) => {
    if (err.response?.status === 401) {
      dispatch({ type: "LOGOUT" });
      navigate("/");
    }
  }, [dispatch, navigate]);

  const getUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${tempUrl}/usersPagination?search_query=${encodeURIComponent(
          searchTerm
        )}&page=${page}&limit=20`,
        {
          _id: user.id,
          token: user.token,
        }
      );
      setUsers(response.data.users);
      setTotalPage(response.data.totalPage);
      setTotalRows(response.data.totalRows);
    } catch (err) {
      handleAuthError(err);
      setError(err.response?.data?.message || "Gagal mengambil data user.");
    }
    setLoading(false);
  }, [handleAuthError, page, searchTerm, user.id, user.token]);

  const getUserById = useCallback(async () => {
    if (!selectedId) {
      setSelectedUser(null);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${tempUrl}/findUser/${selectedId}`, {
        _id: user.id,
        token: user.token,
      });
      setSelectedUser(response.data);
    } catch (err) {
      handleAuthError(err);
      setError(err.response?.data?.message || "Gagal mengambil detail user.");
    }
    setLoading(false);
  }, [handleAuthError, selectedId, user.id, user.token]);

  useEffect(() => {
    getUsers();
  }, [getUsers]);

  useEffect(() => {
    getUserById();
  }, [getUserById]);

  const searchData = (event) => {
    event.preventDefault();
    setPage(0);
    setSearchTerm(query);
  };

  const deleteUser = async (id) => {
    if (String(id) === String(user.id)) return;

    setShowDeleteConfirm(false);
    setLoading(true);
    try {
      await axios.post(`${tempUrl}/users/deleteUser/${id}`, {
        _id: user.id,
        token: user.token,
      });
      navigate("/daftarUser");
      setSelectedUser(null);
      await getUsers();
    } catch (err) {
      handleAuthError(err);
      setDeleteError(err.response?.data?.message || "Gagal menghapus user.");
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div>
      <ScrollToTop />
      <div className="page-header">
        <div>
          <div className="page-kicker">User</div>
          <h1 className="page-title">Daftar User</h1>
        </div>
        <Button className="btn-icon" size="sm" onClick={() => navigate("/daftarUser/tambahUser")}>
          <MdAdd /> Tambah
        </Button>
      </div>
      {error && <Alert variant="warning">{error}</Alert>}

      {selectedUser && (
        <section className="section-panel">
          <div className="toolbar mt-0">
            <div className="detail-title-actions">
              <Button className="btn-back" size="sm" variant="outline-secondary" onClick={() => navigate("/daftarUser")}>
                {"< Kembali"}
              </Button>
              <div>
                <h2 className="h6 fw-bold mb-1">Detail User</h2>
                <div className="text-muted">{selectedUser.username}</div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Button
                className="btn-icon"
                size="sm"
                variant="outline-primary"
                onClick={() => navigate(`/daftarUser/${selectedUser.id}/edit`)}
              >
                <MdEdit /> Ubah
              </Button>
              <Button
                className="btn-icon"
                size="sm"
                variant="outline-danger"
                disabled={String(selectedUser.id) === String(user.id)}
                onClick={() => setShowDeleteConfirm(true)}
              >
                <MdDelete /> Hapus
              </Button>
            </div>
          </div>
          <hr />
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <div className="detail-label">Username</div>
                <div className="detail-value">{selectedUser.username || "-"}</div>
              </div>
              <div className="mb-3">
                <div className="detail-label">Tipe User</div>
                <div className="detail-value">{selectedUser.tipeUser || "-"}</div>
              </div>
            </Col>
            <Col md={6}>
              <h3 className="h6 fw-bold mb-2">Hak Akses</h3>
              <div className="access-menu-groups">
                <div className="access-menu-group">
                  <div className="access-menu-title">Master</div>
                  <Form.Check
                    type="checkbox"
                    label="Kategori"
                    checked={Boolean(selectedUser.akses?.kategori)}
                    disabled
                    readOnly
                  />
                  <Form.Check
                    type="checkbox"
                    label="Merk"
                    checked={Boolean(selectedUser.akses?.merk)}
                    disabled
                    readOnly
                  />
                  <Form.Check
                    type="checkbox"
                    label="Barang"
                    checked={Boolean(selectedUser.akses?.barang)}
                    disabled
                    readOnly
                  />
                  <Form.Check
                    type="checkbox"
                    label="WA Admin"
                    checked={Boolean(selectedUser.akses?.adminWhatsapp)}
                    disabled
                    readOnly
                  />
                  <Form.Check
                    type="checkbox"
                    label="Informasi Toko"
                    checked={Boolean(selectedUser.akses?.informasiToko)}
                    disabled
                    readOnly
                  />
                </div>
                <div className="access-menu-group">
                  <div className="access-menu-title">Transaksi</div>
                  <Form.Check
                    type="checkbox"
                    label="Keranjang"
                    checked={Boolean(selectedUser.akses?.keranjang)}
                    disabled
                    readOnly
                  />
                  <Form.Check
                    type="checkbox"
                    label="Pesanan"
                    checked={Boolean(selectedUser.akses?.pesanan)}
                    disabled
                    readOnly
                  />
                </div>
                <div className="access-menu-group">
                  <div className="access-menu-title">Utility</div>
                  <Form.Check
                    type="checkbox"
                    label="Daftar User"
                    checked={Boolean(selectedUser.akses?.daftarUser)}
                    disabled
                    readOnly
                  />
                </div>
              </div>
            </Col>
          </Row>
        </section>
      )}

      <Form onSubmit={searchData}>
        <div className="toolbar">
          <div className="d-flex gap-2 flex-wrap">
            <SearchBar setSearchTerm={setQuery} value={query} />
            <Button className="btn-icon" type="submit">
              Cari
            </Button>
          </div>
          <div className="text-muted small">Total data: {totalRows}</div>
        </div>
      </Form>

      <ShowTableDaftarUser
        users={users}
        selectedId={selectedId}
        onSelect={(id) => navigate(`/daftarUser/${id}`)}
      />

      {totalPage > 1 && (
        <Pagination className="mt-3">
          <Pagination.Prev
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
          />
          {Array.from({ length: totalPage }, (_, index) => (
            <Pagination.Item
              key={index}
              active={index === page}
              onClick={() => setPage(index)}
            >
              {index + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next
            disabled={page + 1 >= totalPage}
            onClick={() => setPage(page + 1)}
          />
        </Pagination>
      )}

      <ConfirmDeleteDialog
        show={showDeleteConfirm}
        message={`Yakin ingin menghapus user ${selectedUser?.username}?`}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={() => deleteUser(selectedUser.id)}
      />
      <InfoDialog
        show={Boolean(deleteError)}
        title="Data Tidak Bisa Dihapus"
        message={deleteError}
        onClose={() => setDeleteError("")}
      />
    </div>
  );
};

export default DaftarUser;
